import { renderAggregateTile } from '../primitives/aggregate-tile.js';
import { renderDonorTile } from '../primitives/donor-tile.js';
import { renderThermometer } from '../primitives/thermometer.js';
import { extractSignageScene, type SignageScene, type SignageSceneBlock } from '../scene-contract.js';
import { applyBranding, extractBranding, formatCurrency, type Branding } from './branding.js';

export interface StudioSceneRenderState {
  readonly config: Record<string, unknown> | null;
  readonly payload: Record<string, unknown>;
  readonly sessionContent: Record<string, unknown> | null;
  readonly orgContent: Record<string, unknown> | null;
}

const ANNOUNCEMENT_BOARD_LIMIT = 8;

export function renderStudioScene(root: HTMLElement, state: StudioSceneRenderState): boolean {
  const scene = extractSignageScene(state.payload);
  if (!scene) return false;
  const blocks = scene.blocks.filter((block) => block.enabled !== false);

  const branding = extractBranding(sceneBrandingConfig(state.config, scene));
  applyBranding(root, branding);

  root.innerHTML = '';
  root.className = `ag-app ag-scene ag-scene--${cssToken(scene.layout)}`;
  root.dataset.assignedLayout = scene.layout;

  const header = document.createElement('header');
  header.className = 'ag-scene__header';
  const title = document.createElement('div');
  title.className = 'ag-scene__org';
  title.textContent = branding.orgName;
  header.appendChild(title);
  const meta = document.createElement('div');
  meta.className = 'ag-scene__meta';
  meta.textContent = scene.layout.replaceAll('_', ' ');
  header.appendChild(meta);
  root.appendChild(header);

  const stage = document.createElement('main');
  stage.className = 'ag-scene__stage';
  stage.style.setProperty('--ag-scene-block-count', String(Math.max(1, Math.min(blocks.length, 4))));
  if (blocks.length === 0) {
    const empty = document.createElement('section');
    empty.className = 'ag-scene-block ag-scene-block--empty';
    renderEmpty(empty, 'No enabled blocks');
    stage.appendChild(empty);
  } else {
    for (const block of blocks) {
      stage.appendChild(renderBlock(block, scene, state, branding));
    }
  }
  root.appendChild(stage);
  return true;
}

function renderBlock(
  block: SignageSceneBlock,
  scene: SignageScene,
  state: StudioSceneRenderState,
  branding: Branding
): HTMLElement {
  const section = document.createElement('section');
  section.className = `ag-scene-block ag-scene-block--${cssToken(block.kind)}`;
  section.dataset.blockKey = block.key;
  section.dataset.blockKind = block.kind;

  const title = document.createElement('div');
  title.className = 'ag-scene-block__title';
  title.textContent = blockLabel(block.kind);
  section.appendChild(title);

  const body = document.createElement('div');
  body.className = 'ag-scene-block__body';
  section.appendChild(body);

  switch (block.kind) {
    case 'session_progress':
      renderSessionProgress(body, state, branding);
      break;
    case 'session_feed':
      renderDonationFeed(body, liveArray(state.sessionContent, 'feed') ?? sourceItems(scene, 'session_donation_feed'), branding);
      break;
    case 'session_milestones':
      renderMilestones(body, liveArray(state.sessionContent, 'milestones') ?? sourceItems(scene, 'milestones'));
      break;
    case 'session_announcements':
      renderAnnouncements(body, liveArray(state.sessionContent, 'announcements') ?? sourceItems(scene, 'announcements'));
      break;
    case 'donor_wall':
      renderDonorWall(body, liveArray(state.orgContent, 'donor_wall') ?? sourceItems(scene, 'donor_wall'), branding);
      break;
    case 'campaign_summary':
      renderCampaignSummary(body, liveRecord(state.orgContent, 'top_campaign') ?? sourceRecord(scene, 'top_campaign'), branding);
      break;
    case 'org_aggregate':
      renderAggregate(body, liveRecord(state.orgContent, 'aggregate') ?? sourceRecord(scene, 'aggregate'), branding);
      break;
    case 'org_announcements':
      renderAnnouncements(body, liveArray(state.orgContent, 'announcements') ?? sourceItems(scene, 'announcements'));
      break;
    case 'announcements_full':
      renderAnnouncementBoard(body, liveArray(state.orgContent, 'announcements') ?? sourceItems(scene, 'announcements'));
      break;
    case 'maintenance_banner':
      renderMessage(body, 'Display maintenance', 'This display is paused for operator maintenance.');
      break;
    case 'idle_frame':
      renderBrandFrame(body, branding, liveRecord(state.sessionContent, 'qr_live_url') ?? sourceRecord(scene, 'qr_live_url'));
      break;
    default:
      renderSourceSummary(body, block, scene);
      break;
  }
  return section;
}

function renderSessionProgress(
  body: HTMLElement,
  state: StudioSceneRenderState,
  branding: Branding
): void {
  const progress = nonEmptyRecord(state.sessionContent)
    ? state.sessionContent
    : sourceRecord(extractSignageScene(state.payload), 'session_progress') ?? {};
  const name = stringValue(progress['session_name']) ?? stringValue(progress['name']) ?? 'Fundraising';
  const raised =
    numberValue(progress['credited_total_minor_units']) ??
    numberValue(progress['total_raised_minor_units']) ??
    numberValue(progress['goal_progress_minor_units']) ??
    0;
  const goal = numberValue(progress['goal_minor_units']) ?? 0;
  const pct = goal > 0 ? raised / goal : 0;
  appendMetric(body, name, `${formatCurrency(raised, branding)} of ${formatCurrency(goal, branding)}`);
  body.appendChild(renderThermometer({ percent: pct, size: 'lg' }));
}

function renderDonationFeed(
  body: HTMLElement,
  feed: readonly Record<string, unknown>[],
  branding: Branding
): void {
  if (feed.length === 0) {
    renderEmpty(body, 'Waiting for first gift');
    return;
  }
  const list = document.createElement('div');
  list.className = 'ag-scene-list';
  for (const entry of feed.slice(0, 6)) {
    const row = document.createElement('div');
    row.className = 'ag-scene-feed-row';
    const amount = document.createElement('strong');
    amount.textContent = formatCurrency(numberValue(entry['amount_minor_units']) ?? 0, branding);
    row.appendChild(amount);
    const donor = document.createElement('span');
    donor.textContent = stringValue(entry['donor_display']) ?? 'Donor';
    row.appendChild(donor);
    list.appendChild(row);
  }
  body.appendChild(list);
}

function renderMilestones(body: HTMLElement, milestones: readonly Record<string, unknown>[]): void {
  if (milestones.length === 0) {
    renderEmpty(body, 'Milestones will appear here');
    return;
  }
  const grid = document.createElement('div');
  grid.className = 'ag-scene-chip-grid';
  for (const milestone of milestones.slice(0, 6)) {
    const chip = document.createElement('div');
    chip.className = milestone['unlocked'] === true ? 'ag-milestone ag-milestone--unlocked' : 'ag-milestone';
    chip.textContent = stringValue(milestone['title']) ?? 'Milestone';
    grid.appendChild(chip);
  }
  body.appendChild(grid);
}

function renderDonorWall(
  body: HTMLElement,
  wall: readonly Record<string, unknown>[],
  branding: Branding
): void {
  if (wall.length === 0) {
    renderEmpty(body, 'Recent donors will appear here');
    return;
  }
  const grid = document.createElement('div');
  grid.className = 'ag-scene-donor-grid';
  for (const entry of wall.slice(0, 8)) {
    grid.appendChild(
      renderDonorTile({
        donorDisplay: stringValue(entry['donor_display']) ?? 'Donor',
        amountDisplay: formatCurrency(numberValue(entry['amount_minor_units']) ?? 0, branding),
        designation: stringValue(entry['designation_name']),
        source: stringValue(entry['source']),
      })
    );
  }
  body.appendChild(grid);
}

function renderCampaignSummary(
  body: HTMLElement,
  campaign: Record<string, unknown> | null,
  branding: Branding
): void {
  if (!campaign) {
    renderEmpty(body, 'No active campaign');
    return;
  }
  const raised = numberValue(campaign['raised_minor_units']) ?? 0;
  const goal = numberValue(campaign['goal_minor_units']) ?? 0;
  appendMetric(
    body,
    stringValue(campaign['name']) ?? 'Campaign',
    `${formatCurrency(raised, branding)} of ${formatCurrency(goal, branding)}`
  );
  body.appendChild(renderThermometer({ percent: goal > 0 ? raised / goal : 0 }));
}

function renderAggregate(
  body: HTMLElement,
  aggregate: Record<string, unknown> | null,
  branding: Branding
): void {
  const data = aggregate ?? {};
  const grid = document.createElement('div');
  grid.className = 'ag-scene-aggregate-grid';
  grid.appendChild(
    renderAggregateTile({
      label: 'Raised YTD',
      value: formatCurrency(numberValue(data['total_raised_ytd_minor_units']) ?? 0, branding),
    })
  );
  grid.appendChild(renderAggregateTile({ label: 'Donors', value: String(numberValue(data['donor_count']) ?? 0) }));
  grid.appendChild(
    renderAggregateTile({
      label: 'Active Campaigns',
      value: String(numberValue(data['active_campaigns']) ?? 0),
    })
  );
  body.appendChild(grid);
}

function renderAnnouncements(
  body: HTMLElement,
  announcements: readonly Record<string, unknown>[]
): void {
  if (announcements.length === 0) {
    renderEmpty(body, 'Announcements will appear here');
    return;
  }
  const list = document.createElement('div');
  list.className = 'ag-scene-announcements';
  for (const announcement of announcements.slice(0, 3)) {
    const article = document.createElement('article');
    const title = document.createElement('strong');
    title.textContent = announcementTitle(announcement);
    article.appendChild(title);
    const bodyText = announcementBody(announcement);
    if (bodyText) {
      const p = document.createElement('p');
      p.textContent = bodyText;
      article.appendChild(p);
    }
    list.appendChild(article);
  }
  body.appendChild(list);
}

function renderAnnouncementBoard(
  body: HTMLElement,
  announcements: readonly Record<string, unknown>[]
): void {
  if (announcements.length === 0) {
    renderEmpty(body, 'Announcement board is empty');
    return;
  }
  const board = document.createElement('div');
  board.className = 'ag-scene-announcement-board';
  board.dataset.announcementCount = String(announcements.length);

  const meta = document.createElement('div');
  meta.className = 'ag-scene-announcement-board__meta';
  meta.textContent = announcementCountLabel(announcements.length);
  board.appendChild(meta);

  const list = document.createElement('div');
  list.className = 'ag-scene-announcement-board__list';
  for (const announcement of announcements.slice(0, ANNOUNCEMENT_BOARD_LIMIT)) {
    const article = document.createElement('article');
    const title = document.createElement('strong');
    title.textContent = announcementTitle(announcement);
    article.appendChild(title);
    const bodyText = announcementBody(announcement);
    if (bodyText) {
      const p = document.createElement('p');
      p.textContent = bodyText;
      article.appendChild(p);
    }
    list.appendChild(article);
  }
  board.appendChild(list);

  if (announcements.length > ANNOUNCEMENT_BOARD_LIMIT) {
    const overflow = document.createElement('div');
    overflow.className = 'ag-scene-announcement-board__overflow';
    overflow.textContent = `Showing ${ANNOUNCEMENT_BOARD_LIMIT} of ${announcements.length}`;
    board.appendChild(overflow);
  }

  body.appendChild(board);
}

function renderBrandFrame(
  body: HTMLElement,
  branding: Branding,
  qr: Record<string, unknown> | null
): void {
  appendMetric(body, branding.orgName, stringValue(qr?.['url']) ?? 'Scan or visit your giving page');
}

function renderMessage(body: HTMLElement, title: string, subtitle: string): void {
  appendMetric(body, title, subtitle);
}

function renderSourceSummary(body: HTMLElement, block: SignageSceneBlock, scene: SignageScene): void {
  const sources = block.source_keys
    .map((key) => scene.source_bindings.find((source) => source.key === key))
    .filter(Boolean);
  if (sources.length === 0) {
    renderEmpty(body, 'No source binding');
    return;
  }
  for (const source of sources) {
    const row = document.createElement('div');
    row.className = 'ag-scene-source-row';
    row.textContent = `${source!.key}: ${source!.status}`;
    body.appendChild(row);
  }
}

function appendMetric(body: HTMLElement, label: string, value: string): void {
  const metric = document.createElement('div');
  metric.className = 'ag-scene-metric';
  const labelEl = document.createElement('div');
  labelEl.className = 'ag-scene-metric__label';
  labelEl.textContent = label;
  const valueEl = document.createElement('div');
  valueEl.className = 'ag-scene-metric__value';
  valueEl.textContent = value;
  metric.appendChild(labelEl);
  metric.appendChild(valueEl);
  body.appendChild(metric);
}

function renderEmpty(body: HTMLElement, text: string): void {
  const empty = document.createElement('div');
  empty.className = 'ag-scene-empty';
  empty.textContent = text;
  body.appendChild(empty);
}

function sourceItems(scene: SignageScene | null, key: string): readonly Record<string, unknown>[] {
  const data = sourceData(scene, key);
  return Array.isArray(data) ? (data as readonly Record<string, unknown>[]) : [];
}

function sourceRecord(scene: SignageScene | null, key: string): Record<string, unknown> | null {
  return record(sourceData(scene, key));
}

function sourceData(scene: SignageScene | null, key: string): unknown {
  const binding = scene?.source_bindings.find((source) => source.key === key);
  const raw = binding as unknown as Record<string, unknown> | undefined;
  const sample = record(raw?.['sample_data']);
  if (!sample) return null;
  if (Array.isArray(sample['items'])) return sample['items'];
  if ('value' in sample) return sample['value'];
  return sample;
}

function liveArray(
  content: Record<string, unknown> | null,
  key: string
): readonly Record<string, unknown>[] | null {
  const value = content?.[key];
  return Array.isArray(value) ? (value as readonly Record<string, unknown>[]) : null;
}

function liveRecord(content: Record<string, unknown> | null, key: string): Record<string, unknown> | null {
  return record(content?.[key]);
}

function sceneBrandingConfig(
  config: Record<string, unknown> | null,
  scene: SignageScene
): Record<string, unknown> | null {
  const sceneRaw = scene as unknown as Record<string, unknown>;
  if (record(config?.['branding'])) return config;
  const branding = record(sceneRaw['branding']);
  return branding ? { ...(config ?? {}), branding } : config;
}

function cssToken(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'scene';
}

function blockLabel(kind: string): string {
  return kind.replaceAll('_', ' ').replace(/\b\w/g, (match) => match.toUpperCase());
}

function announcementCountLabel(count: number): string {
  return count === 1 ? '1 active announcement' : `${count} active announcements`;
}

function announcementTitle(announcement: Record<string, unknown>): string {
  return stringValue(announcement['title']) ?? stringValue(announcement['message']) ?? 'Announcement';
}

function announcementBody(announcement: Record<string, unknown>): string | null {
  const body = stringValue(announcement['body']);
  if (body) return body;
  const message = stringValue(announcement['message']);
  return message && message !== announcementTitle(announcement) ? message : null;
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function nonEmptyRecord(value: Record<string, unknown> | null): value is Record<string, unknown> {
  return Boolean(value && Object.keys(value).length > 0);
}

function numberValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}
